// @ts-nocheck
import type {
  CartAddVoucherError,
  CartV2,
  CartV2RedPointsRequestV1,
  CartV2VoucherRequestV1,
} from '@redteclab/api/clients/bully'
import { AxiosResponse } from 'axios'
import type { Dispatch } from 'redux'
import { type ThunkAction } from 'redux-thunk'

import { DOCUMENT_TYPE, SA_SELLER_ID, STATE_RESET_MS } from 'shared/consts'
import { BAD_REQUEST } from 'shared/consts/codes'
import { isSaeOnly } from 'shared/experiments/utils/featureFlags'
import trackOptimizelyEvent from 'shared/experiments/utils/trackOptimizelyEvent'
import store from 'shared/store'
import { REFRESH_MODALS } from 'shared/store/ducks/events'
import { updateNowDataSuccessAction } from 'shared/store/ducks/now/actions'
import {
  CartControllerCartEntryItemV2,
  CartControllerCreateCartRequestBodyV2,
  CartControllerGroupEntryV2,
  CartControllerSellerGroupV2,
} from 'types/api/cart'
import { GtmDataLayerCartChanged } from 'types/api/gtm'
import { IState, NowDataState } from 'types/redux'
import fetchMiniCartData from 'views/assets/scripts/fetchMiniCartData'
import { mountSelect } from 'views/assets/scripts/select'
import mountMarketPlaceDiscountModalContainer from 'views/containers/browser/MarketPlaceDiscountModalContainer/mountMarketPlaceDiscountModalContainer'
import mountSellerModalContainer from 'views/containers/browser/SellerModalContainer/mountSellerModalContainer'
import mountVolumePricingModalContainer from 'views/containers/browser/VolumePricingModalContainer/mountVolumePricingModalContainer'
import buildClientAPI from 'views/providers/clientAPI'

import {
  addErrorAction,
  addErrorNotificationAction,
  addVoucherErrorAction,
  addVoucherRequestAction,
  addVoucherSuccessAction,
  CartActionTypes,
  changeCollectiveOrderSuccessAction,
  changePrescriptionFollowsSuccessAction,
  deleteCartERxItemRequestAction,
  deleteCartERxItemSuccessAction,
  deleteCartItemRequestAction,
  deleteCartItemSuccessAction,
  deleteCartOffersAction,
  deleteRedPointsSuccessAction,
  deleteVoucherRequestAction,
  deleteVoucherSuccessAction,
  resetVoucherAction,
  updateCartSuccessAction,
  updateCartVoucherErrorAction,
  updateCollectionOrderSuccessAction,
  updateCollectiveOrderAction,
  updatePrescriptionFollowsAction,
  updateRedPointsErrorAction,
  updateRedPointsRequestAction,
  updateRedPointsSuccessAction,
} from './actions'
import { DELETE_CART_ITEMS } from './types.consts'

type ThunkResult<R> = ThunkAction<R, IState, undefined, CartActionTypes>

const pushToDataLayer = (
  event: string,
  payload: string | GtmDataLayerCartChanged
) =>
  window?.dataLayer.push({
    event,
    payload,
  })

const CART_CHANGE_QUANTITY_EVENT = 'CART_CHANGE_QUANTITY_EVENT'
const CART_DELETE_ITEM_EVENT = 'CART_DELETE_ITEM_EVENT'

// Thunk Actions
export const setCollectiveOrderAction =
  (): ThunkResult<void> => (dispatch: Dispatch, getState: () => IState) => {
    try {
      const collectiveOrderBool = !getState().cart.collectiveOrder
      dispatch(updateCollectionOrderSuccessAction(collectiveOrderBool))
      // get items with overdrafts and update select buttons
      store.dispatch({
        type: REFRESH_MODALS,
        selector: '#h_spaCart',
      })
      // update seller modal container
      mountSellerModalContainer()
    } catch (e) {
      dispatch(addErrorAction(`setCollectiveOrderAction: ${e.message}`))
    }
  }

const getSellerItems = (offerId: string): CartControllerCartEntryItemV2[] =>
  store
    .getState()
    .cart.data.sellerGroups.reduce(
      (
        filtered: CartControllerCartEntryItemV2[],
        seller: CartControllerSellerGroupV2
      ) => {
        const entryGroup = seller.groupEntries.find(entry =>
          entry.items.find(item => item.offerId === offerId)
        )

        if (entryGroup) {
          filtered = filtered.concat(
            seller.groupEntries.reduce(
              (
                items: CartControllerCartEntryItemV2[],
                entry: CartControllerGroupEntryV2
              ) => items.concat(entry.items),
              []
            )
          )
        }
        return filtered
      },
      []
    )

const updateNowInformation =
  (sellerGroups, zipCode, eventType) => (dispatch: Dispatch) => {
    const saeSellerGroupData = sellerGroups.find(e => {
      return e.sellerId === SA_SELLER_ID
    })

    if (saeSellerGroupData?.nowSellerData) {
      const nowSellerData = saeSellerGroupData.nowSellerData

      dispatch(
        updateNowDataSuccessAction({
          ...nowSellerData,
          zipCode,
          eventType,
        })
      )
    } else {
      dispatch(
        updateNowDataSuccessAction({
          zipCode: '',
        })
      )
    }
  }

const updateSelectComponents = (
  sellerItems: CartControllerCartEntryItemV2[]
): void =>
  sellerItems?.forEach((sellerItem: CartControllerCartEntryItemV2) => {
    const element = document.getElementById(
      `offer_select_${sellerItem.offerId}`
    )
    if (element) {
      mountSelect(element)
    }
  })

const getFilteredSaeItems = sellerGroups => {
  const sellerGroup = sellerGroups.find(it => it.sellerId === SA_SELLER_ID)

  return sellerGroup?.groupEntries?.find(e => {
    return e.type === DOCUMENT_TYPE.NO_PRESCRIPTION
  })
}

export const getMergedCartItems = ({ cartData, deleteOfferId }) => {
  const cartItems = []

  const sellerGroups = cartData
    ? cartData?.sellerGroups
    : store.getState().cart.data.sellerGroups

  const noPrescriptionSaeItems = getFilteredSaeItems(sellerGroups)

  noPrescriptionSaeItems?.items?.forEach(item => {
    if (!deleteOfferId || item.offerId !== deleteOfferId) {
      cartItems.push(item)
    }
  })

  return cartItems
}

export const getNowMappedCartItems = (items, cart, quantity) => {
  const products = items.map(item => {
    const itemQuantity =
      cart && quantity && item.offerId === cart.offerId
        ? quantity
        : item.quantity

    return {
      upid: `${item.upid || item.pzn}`,
      priceCentsGross: item.price * 100,
      nowProduct: 0,
      quantity: itemQuantity,
    }
  })

  return products
}

export const updateCartWithNowData =
  (nowResponse): ThunkResult<Promise<void>> =>
  async (dispatch: Dispatch) => {
    const { cartServiceV3, nowService } = buildClientAPI()

    const response: AxiosResponse<CartV2> = await cartServiceV3.fetchCart()

    const eligibleNowProductList = nowService.filterEligibleItemList(
      nowResponse?.products
    )

    const noPrescriptionSaeItems = getFilteredSaeItems(
      response.data.sellerGroups
    )

    noPrescriptionSaeItems?.items?.forEach(item => {
      item.nowProduct = eligibleNowProductList.includes(`${item.upid}`)
    })

    dispatch(updateCartSuccessAction(response.data))
  }

export const updateCartAction =
  (
    quantity: string,
    cart: CartControllerCartEntryItemV2,
    productType: string,
    nowData: NowDataState
  ): ThunkResult<Promise<void>> =>
  async (dispatch: Dispatch) => {
    const {
      pageProperties: { experiments },
      publicConfig: { tenant },
    } = store.getPublicRuntimeConfig()

    try {
      const { cartServiceV3 } = buildClientAPI()
      const submitData: CartControllerCreateCartRequestBodyV2 = {
        channel: cart.channel,
        offerId: cart.offerId,
        overwriteQuantity: true,
        prescriptionType: productType,
        quantity,
      }

      const nowParams = {
        nowZipCode: nowData?.zipCode,
        isNowSelected: false,
      }

      const response: AxiosResponse<CartV2> = await cartServiceV3.createCart(
        submitData,
        true,
        nowParams
      )

      if (isSaeOnly(experiments, tenant)) {
        await fetchMiniCartData()
      }

      dispatch(updateCartSuccessAction(response.data))

      dispatch(
        updateNowInformation(
          response.data.sellerGroups,
          nowData?.zipCode,
          'update'
        )
      )

      // dispatch update shipping cost modals
      store.dispatch({
        type: REFRESH_MODALS,
        selector: '#h_spaCart',
      })
      // update seller modal container
      mountSellerModalContainer()
      //update volume pricing modals
      await mountVolumePricingModalContainer()
      // update marketplace discount modals
      await mountMarketPlaceDiscountModalContainer()
      // update quantity select component for all seller offers
      updateSelectComponents(getSellerItems(cart.offerId))
      pushToDataLayer('cart.changed', {
        quantity: submitData.quantity,
        id: submitData.offerId,
      })

      trackOptimizelyEvent(CART_CHANGE_QUANTITY_EVENT)
    } catch (e) {
      dispatch(addErrorAction(`updateCartAction: ${e.message}`))
    }
  }

interface INowRequestParams {
  nowZipCode?: string
  isNowSelected?: boolean
  eventType?: string
}

const handleNowDataUpdate =
  (data, nowRequestParams): ThunkResult<Promise<void>> =>
  (dispatch: Dispatch) => {
    if (
      data.sellerGroups.length &&
      (nowRequestParams.nowZipCode || nowRequestParams.nowZipCode === '')
    ) {
      dispatch(
        updateNowInformation(
          data.sellerGroups,
          nowRequestParams?.nowZipCode,
          nowRequestParams?.eventType
        )
      )
    }
  }

export const fetchCartAction =
  (
    purgeMessages?: boolean,
    reloadSelect?: boolean,
    nowRequestParams?: INowRequestParams,
    dontRefreshModals?: boolean
  ): ThunkResult<Promise<void>> =>
  async (dispatch: Dispatch) => {
    try {
      const { cartServiceV3 } = buildClientAPI()
      const sellerIdsBeforeFetch = reloadSelect
        ? store
            .getState()
            .cart.data.sellerGroups.filter(sellerGroup =>
              sellerGroup.groupEntries.find(
                groupEntry => groupEntry.items.length > 1
              )
            )
        : []

      const response: AxiosResponse<CartV2> = await cartServiceV3.fetchCart(
        purgeMessages,
        nowRequestParams?.nowZipCode,
        nowRequestParams?.isNowSelected
      )
      const { data } = response

      dispatch(updateCartSuccessAction(data))

      dispatch(handleNowDataUpdate(data, nowRequestParams))

      if (!dontRefreshModals) {
        store.dispatch({
          type: REFRESH_MODALS,
          selector: '#h_spaCart',
        })
      }

      mountSellerModalContainer()
      await mountVolumePricingModalContainer()
      await mountMarketPlaceDiscountModalContainer()

      if (sellerIdsBeforeFetch.length) {
        const sellerItems = data.sellerGroups.reduce(
          (
            sellerGroupItems: CartControllerCartEntryItemV2[],
            sellerGroup: CartControllerSellerGroupV2
          ) => {
            const sellerGroupForUpdate = sellerIdsBeforeFetch.find(
              sellerGroupBeforeFetch =>
                sellerGroupBeforeFetch.sellerId === sellerGroup.sellerId
            )
            if (sellerGroupForUpdate) {
              sellerGroupItems = sellerGroupItems.concat(
                sellerGroup.groupEntries.reduce(
                  (
                    items: CartControllerCartEntryItemV2[],
                    entry: CartControllerGroupEntryV2
                  ) => {
                    if (
                      sellerGroupForUpdate.groupEntries.find(
                        groupEntry =>
                          groupEntry.type === entry.type &&
                          entry.items.length < groupEntry.items.length
                      )
                    ) {
                      items = items.concat(entry.items)
                    }
                    return items
                  },
                  []
                )
              )
            }
            return sellerGroupItems
          },
          []
        )
        if (sellerItems.length) {
          updateSelectComponents(sellerItems)
        }
      }
    } catch (e) {
      dispatch(addErrorAction(`fetchCartAction: ${e.message}`))
    }
  }

// Locking is necessary for when the user performs the action multiple times and some of them fail.
let prescriptionFollowslocked = false
export const putPrescriptionFollowsAction =
  (prescriptionFollows: boolean): ThunkResult<Promise<void>> =>
  async (dispatch: Dispatch) => {
    if (prescriptionFollowslocked) {
      return
    }

    prescriptionFollowslocked = true

    try {
      dispatch(updatePrescriptionFollowsAction(prescriptionFollows))
      const { cartServiceV3 } = buildClientAPI()
      const nowData = store.getState().nowData
      const nowRequestParams: NowRequestParamsType = {
        nowZipCode: nowData?.zipCode,
        isNowSelected: false,
      }
      const response: AxiosResponse<CartV2> = await cartServiceV3.putCart(
        {
          prescriptionFollows,
        },
        nowRequestParams
      )
      dispatch(changePrescriptionFollowsSuccessAction())
      dispatch(updateCartSuccessAction(response.data))
    } catch (e) {
      dispatch(updatePrescriptionFollowsAction(!prescriptionFollows))
      dispatch(addErrorAction(`putPrescriptionFollowsAction: ${e.message}`))
    } finally {
      prescriptionFollowslocked = false
    }
  }

// Locking is necessary for when the user performs the action multiple times and some of them fail.
let collectiveOrderlocked = false
export const putCollectiveOrderAction =
  (collectiveOrder: boolean): ThunkResult<Promise<void>> =>
  async (dispatch: Dispatch) => {
    if (collectiveOrderlocked) {
      return
    }

    collectiveOrderlocked = true

    try {
      dispatch(updateCollectiveOrderAction(collectiveOrder))
      const { cartServiceV3 } = buildClientAPI()
      const nowData = store.getState().nowData
      const nowRequestParams: NowRequestParamsType = {
        nowZipCode: nowData?.zipCode,
        isNowSelected: false,
      }
      const response: AxiosResponse<CartV2> = await cartServiceV3.putCart(
        {
          collectiveOrder,
        },
        nowRequestParams
      )
      dispatch(changeCollectiveOrderSuccessAction())
      dispatch(updateCartSuccessAction(response.data))
    } catch (e) {
      dispatch(updateCollectiveOrderAction(!collectiveOrder))
      dispatch(addErrorAction(`putCollectiveOrderAction: ${e.message}`))
    } finally {
      collectiveOrderlocked = false

      // dispatch update shipping cost modals
      store.dispatch({
        type: REFRESH_MODALS,
        selector: '#h_spaCart',
      })
      // update seller modal container
      mountSellerModalContainer()
      // get all sae seller select items and update it
      const sellerItems = store
        .getState()
        .cart.data.sellerGroups.reduce(
          (
            filtered: CartControllerCartEntryItemV2[],
            seller: CartControllerSellerGroupV2
          ) => {
            if (seller.sellerId === SA_SELLER_ID) {
              filtered = filtered.concat(
                seller.groupEntries.reduce(
                  (
                    items: CartControllerCartEntryItemV2[],
                    entry: CartControllerGroupEntryV2
                  ) => items.concat(entry.items),
                  []
                )
              )
            }
            return filtered
          },
          []
        )
      updateSelectComponents(sellerItems)
    }
  }

export const deleteCartItemAction =
  (): ThunkResult<Promise<void>> => async (dispatch: Dispatch) => {
    try {
      const { offers } = store.getState().cart
      const {
        pageProperties: { experiments },
        publicConfig: { tenant },
      } = store.getPublicRuntimeConfig()

      // check if delete is in progress to avoid parallel pending request
      if (!window.sessionStorage.getItem(DELETE_CART_ITEMS)) {
        // execute the request one by one from the queue
        while (offers.length > 0) {
          const currentRequest = offers.shift()
          if (currentRequest) {
            const { offerId, pType } = currentRequest
            // set flag when we send request to delete cart item
            window.sessionStorage.setItem(DELETE_CART_ITEMS, 'loading')

            dispatch(deleteCartItemRequestAction())
            const { cartServiceV3 } = buildClientAPI()
            try {
              await cartServiceV3.deleteCartItem(offerId, pType)
            } catch (_e) {
              // Do nothing, temp solution for 400 response
            }

            window.sessionStorage.removeItem(DELETE_CART_ITEMS)

            dispatch(deleteCartOffersAction(offers))
            dispatch(deleteCartItemSuccessAction())

            pushToDataLayer('item.deleted', offerId)
          }
        }
      }

      // call get data only once after all delete requests are completed
      if (offers.length == 0) {
        if (isSaeOnly(experiments, tenant)) {
          await fetchMiniCartData()
        }

        const nowData = store.getState().nowData

        const nowRequestParams: NowRequestParamsType = {
          nowZipCode: nowData?.zipCode,
          isNowSelected: false,
          eventType: 'delete',
        }

        dispatch(fetchCartAction(false, false, nowRequestParams))
      }

      trackOptimizelyEvent(CART_DELETE_ITEM_EVENT)
    } catch (e) {
      dispatch(addErrorAction(`deleteCartItemAction: ${e.message}`))
      window.sessionStorage.removeItem(DELETE_CART_ITEMS)
    }
  }

export const addVoucherAction =
  (voucherCode: string): ThunkResult<Promise<void>> =>
  async (dispatch: Dispatch) => {
    try {
      if (!voucherCode.length) return
      dispatch(addVoucherRequestAction())
      const voucherPayload: CartV2VoucherRequestV1 = {
        voucherCode,
      }

      const nowData = store.getState().nowData

      const nowRequestParams: NowRequestParamsType = {
        nowZipCode: nowData?.zipCode,
        isNowSelected: false,
      }

      const { cartServiceV3 } = buildClientAPI()
      const response: AxiosResponse<CartV2 | CartAddVoucherError> =
        await cartServiceV3.addVoucher(voucherPayload, true, nowRequestParams)

      dispatch(addVoucherSuccessAction())

      setTimeout(() => dispatch(resetVoucherAction()), STATE_RESET_MS)
      dispatch(updateCartSuccessAction(response.data))

      dispatch(
        updateNowInformation(
          response?.data?.sellerGroups,
          nowRequestParams?.nowZipCode,
          nowRequestParams?.eventType
        )
      )

      pushToDataLayer('voucher.added', voucherPayload.voucherCode)
    } catch (e) {
      const response = e?.response
      const status = response?.status

      if (status === BAD_REQUEST) {
        // Currently only one voucher can be redeemed at a time, this will be extended to multiple vouchers concurrently
        dispatch(addVoucherErrorAction())
        const voucherError = response.data.invalidParams[0].key
        const promotionErrorMessage =
          response.data.invalidParams[0].promotionInformation?.rules
            ?.flatMap(r => [r.message, ...r.conditions.map(c => c.message)])
            .filter(Boolean)
            .join(' ')
        dispatch(
          updateCartVoucherErrorAction(voucherError, promotionErrorMessage)
        )
      } else {
        dispatch(addErrorAction(`addVoucherAction: ${e.message}`))
      }
    }
  }

export const deleteVoucherAction =
  (code: string): ThunkResult<void> =>
  async (dispatch: Dispatch) => {
    try {
      dispatch(deleteVoucherRequestAction())
      const { cartServiceV3 } = buildClientAPI()
      await cartServiceV3.deleteVoucher(code)

      const nowData = store.getState().nowData
      const nowRequestParams: NowRequestParamsType = {
        nowZipCode: nowData?.zipCode,
        isNowSelected: false,
      }

      dispatch(fetchCartAction(true, false, nowRequestParams))
      dispatch(deleteVoucherSuccessAction())

      pushToDataLayer('voucher.deleted', code)
    } catch (e) {
      dispatch(addErrorAction(`deleteVoucherAction: ${e.message}`))
    }
  }

export const deleteCartERxItemAction =
  (hashedToken: string): ThunkResult<Promise<void>> =>
  async (dispatch: Dispatch) => {
    try {
      dispatch(deleteCartERxItemRequestAction())
      const { cartService } = buildClientAPI()
      await cartService.deleteCartERxItem(hashedToken)

      const nowData = store.getState().nowData

      const nowRequestParams: NowRequestParamsType = {
        nowZipCode: nowData?.zipCode,
        isNowSelected: false,
        eventType: 'delete',
      }

      dispatch(fetchCartAction(false, false, nowRequestParams))
      dispatch(deleteCartERxItemSuccessAction())
      store.dispatch({
        type: REFRESH_MODALS,
        selector: '#h_spaCart',
      })
      mountSellerModalContainer()
      pushToDataLayer('eRxItem.deleted', hashedToken)
      await fetchMiniCartData()
    } catch (e) {
      dispatch(addErrorAction(`deleteCartERxItemAction: ${e.message}`))
    }
  }

export const deleteRedPointsAction =
  (): ThunkResult<Promise<void>> => async (dispatch: Dispatch) => {
    try {
      dispatch(updateRedPointsRequestAction())
      const { cartServiceV3 } = buildClientAPI()

      const nowData = store.getState().nowData

      const nowRequestParams: NowRequestParamsType = {
        nowZipCode: nowData?.zipCode,
        isNowSelected: false,
      }

      const response: Promise<AxiosResponse<CartV2>> =
        await cartServiceV3.deleteRedPoints(nowRequestParams)

      dispatch(deleteRedPointsSuccessAction())
      dispatch(updateCartSuccessAction(response.data))
      dispatch(updateRedPointsSuccessAction())

      pushToDataLayer('voucher.deleted', 'redpoints')
    } catch (e) {
      dispatch(updateRedPointsErrorAction())
      dispatch(addErrorAction(`deleteRedPointsAction: ${e.message}`))
    }
  }

export const addRedPointsAction =
  (redpointsAmount: number): ThunkResult<Promise<void>> =>
  async (dispatch: Dispatch) => {
    try {
      if (!redpointsAmount) return
      dispatch(updateRedPointsRequestAction())
      const rpAmount: CartV2RedPointsRequestV1 = { redpointsAmount }
      const { cartServiceV3 } = buildClientAPI()

      const nowData = store.getState().nowData

      const nowRequestParams: NowRequestParamsType = {
        nowZipCode: nowData?.zipCode,
        isNowSelected: false,
      }

      const response: Promise<AxiosResponse<CartV2>> =
        await cartServiceV3.addRedPoints(rpAmount, nowRequestParams)
      dispatch(updateRedPointsSuccessAction())
      dispatch(updateCartSuccessAction(response.data))

      pushToDataLayer('voucher.added', `redpoints${redpointsAmount}`)
    } catch (e) {
      dispatch(updateRedPointsErrorAction())
      dispatch(
        addErrorNotificationAction(e.response?.data?.message ?? 'unknown error')
      )
      dispatch(addErrorAction(`addRedPointsAction: ${e.message}`))
    }
  }
